import type { ContentProductPage } from "~/models/Content/ContentProductPage"
import type {
  ProductInformation,
  ProductsList,
  ProductVariants,
} from "~/models/Content/ContentProducts"
import type { Licence } from "~/types/licence"
import type { VariantKeys } from "~/utilities/constants"
import { ContentType } from "~/models/Content/ContentType"
import {
  formatList,
  parseInstitutionNameAndCode,
} from "~/utilities/format"
import { extractProductVariant } from "~/utilities/format-product"

// Simple way of getting all fields in ProductsList that should come from the product page
type SharedKeys = keyof ProductsList & keyof ContentProductPage

export function getGroupedProductVariants(
  productVariants: ProductVariants[] = [],
  productPages: ContentProductPage[] = [],
  activeLicences: Licence[] | undefined,
): Record<string, ProductsList> {
  const pages: Record<string, Pick<ProductsList, SharedKeys>> = {}

  for (const page of productPages) {
    const productKey = formatForURL(page.title.replaceAll(" ", "-").toLowerCase())

    pages[productKey] = {
      theme: page.theme,
      header: page.header?.html5,
      quote: page.quote,
      groupSalesPitch: page.groupSalesPitch?.html5,
      productGroupStatusDetails: page.productGroupStatusDetails,
      uspList: page.children.find(isContentType(ContentType.ProductPageUSP))?.uspList,
      blocks: page.children.filter(
        isContentType(
          ContentType.ProductPageBlock,
          ContentType.ProductPageMediaTypes,
        ),
      ),
    }
  }

  const products: Record<string, ProductsList> = {}

  for (const productVariant of productVariants) {
    const fullProductTitle = productVariant.ibexaTitle
    const institutionCode = extractProductVariant(fullProductTitle)

    if (!institutionCode)
      continue

    const productTitle = fullProductTitle.replace(
      ` ${institutionCode}`,
      "",
    )

    const productKey = buildProductKey(productVariant.ibexaTitle)

    const parsedCurriculumInfo = parseInstitutionNameAndCode(
      productVariant.pensumFor,
    )

    // institute is for example "Universitetet i Oslo (UiO)" we want to extract the short name
    // and will return "UiO"
    const institutionAcronyms = formatList(
      productVariant.institute.map((i) => {
        const startIndex = i.indexOf("(")
        const endIndex = i.indexOf(")")
        if (startIndex !== -1 && endIndex !== -1) {
          return i.substring(startIndex + 1, endIndex)
        }
        return i
      }),
    )

    const isReleased =
      productVariant.status === "full-release" ||
      productVariant.status === "partial-release"

    const licence = activeLicences?.find(l => l.ean === productVariant.ean)

    const variant: ProductInformation = {
      name: productVariant.ibexaTitle,
      shortTitle: productVariant.shortTitle,
      titleHtml: productVariant.titleHtml,
      title: productVariant.title,
      ean: productVariant.ean,
      curriculumFor: institutionAcronyms,
      isReleased,
      status: productVariant.status,
      variantKey: institutionCode.toLowerCase() as VariantKeys,
      shortVariantLabel: institutionCode,
      locationId: productVariant._location.id,
      pathString: productVariant._location.pathString,
      isFree: productVariant.isFree,
      licence,
      userHasLicence: activeLicences ? !!licence : undefined,
    }

    const existing = products[productKey]

    if (!existing) {
      products[productKey] = {
        ...pages[productKey],
        productKey,
        productTitle,
        productTitleHtml: productVariant.titleHtml,
        urlFormatProductTitle: formatForURL(productTitle),
        hasAtLeastOneReleasedProduct: isReleased,
        productCurriculumFor:
        parsedCurriculumInfo.shortNamesAndCourseCodes,
        vanitySentence: parsedCurriculumInfo.vanitySentence,
        variants: [variant],
      }
    } else {
      products[productKey] = {
        ...existing,
        productCurriculumFor: [
          ...existing.productCurriculumFor,
          ...parsedCurriculumInfo.shortNamesAndCourseCodes,
        ],
        variants: [...existing.variants, variant].sort((a, b) =>
          a.shortVariantLabel.localeCompare(b.shortVariantLabel),
        ),
        vanitySentence: parsedCurriculumInfo.vanitySentence,
        hasAtLeastOneReleasedProduct:
          existing.hasAtLeastOneReleasedProduct || isReleased,
        productTitleHtml:
          existing.productTitleHtml || productVariant.titleHtml,
      }
    }
  }

  return products
}
